import { useState, useEffect, useCallback, useRef } from 'react';
import { graphApi } from '../services/api/graph.service';
import { GraphAnnotation, GraphScope } from '../types/graph.types';

interface UseGraphAnnotationsOptions {
  nodeId?: string;
  edgeId?: string;
  scope: GraphScope;
  enabled?: boolean;
}

interface UseGraphAnnotationsReturn {
  annotations: GraphAnnotation[];
  loading: boolean;
  error: string | null;
  createAnnotation: (content: string, parentId?: string, mentions?: string[]) => Promise<void>;
  updateAnnotation: (annotationId: string, content: string) => Promise<void>;
  resolveAnnotation: (annotationId: string) => Promise<void>;
  deleteAnnotation: (annotationId: string) => Promise<void>;
  refresh: () => void;
}

/**
 * Hook for managing graph annotations on a specific node or edge.
 * Handles fetching, CRUD operations, and optimistic updates.
 */
export function useGraphAnnotations({
  nodeId,
  edgeId,
  scope,
  enabled = true,
}: UseGraphAnnotationsOptions): UseGraphAnnotationsReturn {
  const [annotations, setAnnotations] = useState<GraphAnnotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef<string | null>(null);

  // Fetch annotations for the current node/edge
  const fetchAnnotations = useCallback(async () => {
    if (!enabled || (!nodeId && !edgeId)) return;

    const key = `${nodeId || ''}:${edgeId || ''}:${scope}`;
    // Avoid duplicate fetches for the same target
    if (fetchedRef.current === key && annotations.length > 0) return;

    setLoading(true);
    setError(null);

    try {
      const response = await graphApi.getAnnotations(nodeId, edgeId, scope);
      setAnnotations(response.data || []);
      fetchedRef.current = key;
    } catch (err) {
      setError('Failed to load annotations');
      console.error('Error fetching annotations:', err);
    } finally {
      setLoading(false);
    }
  }, [nodeId, edgeId, scope, enabled, annotations.length]);

  // Fetch on mount and when target changes
  useEffect(() => {
    fetchedRef.current = null; // Reset when target changes
    fetchAnnotations();
  }, [fetchAnnotations]);

  // Create a new annotation
  const createAnnotation = useCallback(async (
    content: string,
    parentId?: string,
    mentions?: string[]
  ) => {
    if (!nodeId && !edgeId) return;

    try {
      const response = await graphApi.createAnnotation(
        nodeId,
        edgeId,
        scope,
        content,
        parentId,
        mentions
      );

      // Optimistic update: add to list
      const newAnnotation = response.data;
      if (parentId) {
        // Reply: add to parent's replies array
        setAnnotations(prev =>
          prev.map(a =>
            a.id === parentId
              ? { ...a, replies: [...(a.replies || []), newAnnotation] }
              : a
          )
        );
      } else {
        // Top-level: prepend to list
        setAnnotations(prev => [newAnnotation, ...prev]);
      }
    } catch (err) {
      setError('Failed to create annotation');
      console.error('Error creating annotation:', err);
      throw err;
    }
  }, [nodeId, edgeId, scope]);

  // Update an annotation
  const updateAnnotation = useCallback(async (annotationId: string, content: string) => {
    try {
      const response = await graphApi.updateAnnotation(annotationId, content);

      // Optimistic update
      setAnnotations(prev =>
        prev.map(a =>
          a.id === annotationId ? { ...a, content: response.data.content } : a
        )
      );
    } catch (err) {
      setError('Failed to update annotation');
      console.error('Error updating annotation:', err);
      throw err;
    }
  }, []);

  // Resolve an annotation
  const resolveAnnotation = useCallback(async (annotationId: string) => {
    try {
      await graphApi.resolveAnnotation(annotationId);

      // Optimistic update
      setAnnotations(prev =>
        prev.map(a =>
          a.id === annotationId
            ? { ...a, status: 'resolved' as const, resolvedAt: new Date().toISOString() }
            : a
        )
      );
    } catch (err) {
      setError('Failed to resolve annotation');
      console.error('Error resolving annotation:', err);
      throw err;
    }
  }, []);

  // Delete an annotation
  const deleteAnnotation = useCallback(async (annotationId: string) => {
    try {
      await graphApi.deleteAnnotation(annotationId);

      // Optimistic update: remove from list
      setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    } catch (err) {
      setError('Failed to delete annotation');
      console.error('Error deleting annotation:', err);
      throw err;
    }
  }, []);

  // Manual refresh
  const refresh = useCallback(() => {
    fetchedRef.current = null;
    fetchAnnotations();
  }, [fetchAnnotations]);

  return {
    annotations,
    loading,
    error,
    createAnnotation,
    updateAnnotation,
    resolveAnnotation,
    deleteAnnotation,
    refresh,
  };
}
